/**
 * BlockRenderer — renderer dinamico unico usado tanto pela pagina publica
 * (SantinhoPage) quanto pelo editor WYSIWYG (preview).
 *
 * Recebe a lista de blocks e contexto. Resolve cada bloco via BlockRegistry
 * e renderiza em ordem. Blocos com visible: false sao filtrados quando NOT
 * em modo editor.
 */

import { motion } from 'framer-motion';
import type { Block } from '@/types/tenant.types';
import { getBlockDefinition } from './registry';
import type { BlockRenderContext } from './types';

interface BlockRendererProps {
  blocks: Block[];
  ctx: BlockRenderContext;
  /** Habilita animacao de entrada com framer-motion (default true). */
  animate?: boolean;
}

export function BlockRenderer({ blocks, ctx, animate = true }: BlockRendererProps) {
  const visibleBlocks = ctx.isEditor
    ? [...blocks].sort((a, b) => a.order - b.order)
    : blocks.filter((b) => b.visible).sort((a, b) => a.order - b.order);

  if (visibleBlocks.length === 0) {
    return null;
  }

  return (
    <>
      {visibleBlocks.map((block, idx) => {
        const def = getBlockDefinition(block.type);
        if (!def) {
          if (ctx.isEditor) {
            return (
              <div
                key={block.id}
                className="container mx-auto px-4 py-4 max-w-5xl text-sm text-red-600"
              >
                Bloco desconhecido: {block.type}
              </div>
            );
          }
          return null;
        }
        const Component = def.Render;

        if (!animate) {
          return <Component key={block.id} block={block} ctx={ctx} />;
        }

        return (
          <motion.div
            key={block.id}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.4, delay: Math.min(idx * 0.05, 0.3) }}
            className={!block.visible && ctx.isEditor ? 'opacity-40' : ''}
          >
            <Component block={block} ctx={ctx} />
          </motion.div>
        );
      })}
    </>
  );
}
